/**
 * awe-buy-button-price.js
 *
 * Keeps the ATC button price in sync with the selected variant, Goddess Circle
 * membership state, and upsell bundle selection. PDP only.
 *
 * Reactive to:
 *   - variant:update        (Horizon variant picker)
 *   - awe:membership:changed (PDP goddess box toggle)
 *   - membership:changed     (cart drawer toggle)
 *   - data-upsell-selected   (upsell bundle MutationObserver)
 */

(function () {
  'use strict';

  /** @type {number} Current main-product variant price in Shopify cents */
  let mainPriceCents = 0;

  /** @type {boolean} Whether the customer is an active subscriber (server-side) */
  let isActiveMember = false;

  /** @type {Element|null} Closest .shopify-section for scoped event listening */
  let section = null;

  /** @type {MutationObserver|null} Watches upsell data-attribute changes */
  let upsellObserver = null;

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  // 'non-member' preferences older than this are treated as expired so the
  // shopper is re-offered membership. Keep in sync with the same constant in
  // awe-cart-membership.js / awe-pdp-membership.js.
  var AWE_MEMBERSHIP_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  /**
   * Reads the shared 'awe:membership' localStorage preference, expiring a
   * 'non-member' choice after AWE_MEMBERSHIP_TTL_MS. Migrates legacy
   * bare-string values (pre-dating expiration) to the timestamped format.
   * @returns {'member'|'non-member'|null}
   */
  function readMembershipPreference() {
    var raw = localStorage.getItem('awe:membership');
    if (!raw) return null;

    var value;
    var ts;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && (parsed.value === 'member' || parsed.value === 'non-member') && typeof parsed.ts === 'number') {
        value = parsed.value;
        ts = parsed.ts;
      }
    } catch {
      // legacy bare-string value, handled below
    }

    if (value === undefined) {
      if (raw !== 'member' && raw !== 'non-member') return null;
      value = raw;
      ts = Date.now();
      try {
        localStorage.setItem('awe:membership', JSON.stringify({ value: value, ts: ts }));
      } catch {
        /* private browsing */
      }
    }

    if (value === 'non-member' && Date.now() - ts > AWE_MEMBERSHIP_TTL_MS) {
      try {
        localStorage.removeItem('awe:membership');
      } catch {
        /* private browsing */
      }
      return null;
    }

    return value;
  }

  function isMember() {
    if (isActiveMember) return true;

    // TEMP override: force non-member
    const FORCE_NON_MEMBER = true;

    if (FORCE_NON_MEMBER) return false;

    try {
      var preference = readMembershipPreference();
      return preference !== 'non-member';
    } catch {
      return true; // default: member
    }
  }

  function getUpsellPriceCents() {
    const el = document.querySelector(
      'awe-product-upsell-bundle[data-upsell-selected="true"]'
    );
    if (!el) return 0;

    const variantId = Number(el.dataset.upsellVariantId);
    if (!variantId) return 0;

    const dataEl = el.querySelector('.js-upsell-variants-data');
    if (!dataEl) return 0;

    try {
      const variants = JSON.parse(dataEl.textContent);
      const v = variants.find(function (item) { return item.id === variantId; });
      return v ? (v.price || 0) : 0;
    } catch {
      return 0;
    }
  }

  function formatMoney(cents) {
    var rounded = Math.round(cents);
    var priceSpan = document.querySelector('.awe-atc-price');
    var currencyCode = (priceSpan && priceSpan.dataset.currencyCode) || 'USD';
    var currencySymbol = (priceSpan && priceSpan.dataset.currencySymbol) || '$';

    var formatted;
    if (window.Shopify && window.Shopify.formatMoney) {
      // Shopify.formatMoney handles number formatting (thousands separators etc.)
      // but may use the store's default $ symbol instead of the active market's symbol
      formatted = window.Shopify.formatMoney(rounded)
        .replace(/\.00$/, '')
        .replace(/\.0$/, '');

      // If the active currency has a different symbol but Shopify returned $, swap it
      if (currencySymbol !== '$' && formatted.startsWith('$')) {
        formatted = currencySymbol + formatted.slice(1);
      }
    } else {
      var dollars = rounded / 100;
      var amount = dollars % 1 === 0 ? dollars.toString() : dollars.toFixed(2);
      formatted = currencySymbol + amount;
    }

    // Append AUD/CAD code — both share $ symbol which confuses non-US customers
    if (currencyCode === 'AUD' || currencyCode === 'CAD') {
      formatted = formatted + ' ' + currencyCode;
    }

    return formatted;
  }

  /* ── Core update ─────────────────────────────────────────────────────── */

  function update() {
    var priceSpan = document.querySelector('.awe-atc-price');
    if (!priceSpan) return;

    // Re-read main price from data attr (morphed by server on variant change)
    var attrPrice = parseInt(priceSpan.dataset.variantPrice, 10);
    if (attrPrice > 0) mainPriceCents = attrPrice;

    var upsellCents = getUpsellPriceCents();
    var total = mainPriceCents + upsellCents;

    if (isMember()) {
      total = Math.round(total * 0.9);
    }

    priceSpan.textContent = ' - ' + formatMoney(total);
  }

  /* ── Init ─────────────────────────────────────────────────────────────── */

  function init() {
    var priceSpan = document.querySelector('.awe-atc-price');
    if (!priceSpan) return;

    // Seed from data attributes
    mainPriceCents = parseInt(priceSpan.dataset.variantPrice, 10) || 0;
    isActiveMember = priceSpan.dataset.isActiveMember === 'true';

    section = priceSpan.closest('.shopify-section');

    // Initial update (adjust for localStorage membership state)
    update();

    // 1. Variant change — wait one frame for morph to complete
    if (section) {
      section.addEventListener('variant:update', function (event) {
        // Read price from event detail for immediate use
        var resource = event.detail && event.detail.resource;
        if (resource && resource.price) {
          mainPriceCents = resource.price;
        }
        requestAnimationFrame(update);
      });
    }

    // 2. PDP goddess box toggle
    document.addEventListener('awe:membership:changed', function () {
      update();
    });

    // 3. Cart drawer membership toggle
    document.addEventListener('membership:changed', function () {
      update();
    });

    // 4. Upsell bundle selection changes
    var upsellEl = document.querySelector('awe-product-upsell-bundle');
    if (upsellEl) {
      upsellObserver = new MutationObserver(function () {
        update();
      });
      upsellObserver.observe(upsellEl, {
        attributes: true,
        attributeFilter: ['data-upsell-selected', 'data-upsell-variant-id']
      });
    }
  }

  /* ── Bootstrap ────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
